/**
 * About component for Manatee Labs website.
 * Follows the design specifications from manatee-labs-full-spec.md
 */
import { motion } from "framer-motion";
import { Award, Cpu, Database, Server } from "lucide-react";
import "./about.css";
import { CertificatesView } from "../certificates/certificatesView";

export const About = () => {
  // Animation variants for fade-in effect
  const fadeIn = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.6 },
    },
  };

  // Expertise items
  const expertiseItems = [
    {
      id: "aws-engineers",
      text: "AWS Certified Engineers",
      icon: "award",
    },
    {
      id: "ai-specialists",
      text: "AI/ML Specialists for Call Routing and Data Insights",
      icon: "cpu",
    },
    {
      id: "infrastructure",
      text: "End-to-End AWS Infrastructure Builders",
      icon: "server",
    },
    {
      id: "trusted",
      text: "Trusted by Enterprises & Startups Alike",
      icon: "database",
    },
  ];

  // Render icon based on icon name
  const renderIcon = (iconName: string) => {
    switch (iconName) {
      case "award":
        return <Award size={24} color="#8E9D5E" />;
      case "cpu":
        return <Cpu size={24} color="#8E9D5E" />;
      case "server":
        return <Server size={24} color="#8E9D5E" />;
      case "database":
        return <Database size={24} color="#8E9D5E" />;
      default:
        return null;
    }
  };

  return (
    <div>
      <section id="about" className="about-section">
        <div className="container">
          <motion.div
            className="about-header"
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={fadeIn}
          >
            <h2 className="about-title">
              Empowering Businesses with Intelligent Call Center Automation
            </h2>
          </motion.div>

          <motion.div
            className="mission-statement"
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={{
              hidden: { opacity: 0, y: 20 },
              visible: {
                opacity: 1,
                y: 0,
                transition: {
                  duration: 0.6,
                  delay: 0.2,
                },
              },
            }}
          >
            <p>
              At Manatee Labs, we believe in simplifying contact center
              technology through automation and AI. Our solutions make Amazon
              Connect deployments seamless, efficient, and scalable.
            </p>
          </motion.div>

          <motion.h3
            className="expertise-title"
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={fadeIn}
          >
            Our Expertise
          </motion.h3>

          <div className="expertise-list">
            {expertiseItems.map((item, index) => (
              <motion.div
                className="expertise-item"
                key={item.id}
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true }}
                variants={{
                  hidden: { opacity: 0, y: 20 },
                  visible: {
                    opacity: 1,
                    y: 0,
                    transition: {
                      duration: 0.6,
                      delay: index * 0.1 + 0.3,
                    },
                  },
                }}
              >
                <div className="expertise-icon">{renderIcon(item.icon)}</div>
                <div className="expertise-text">{item.text}</div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      <section id="founder" className="founder-section">
        <div className="container">
          <motion.h3
            className="founder-title"
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={fadeIn}
          >
            Meet Our Founder
          </motion.h3>

          <div className="founder-content">
            <motion.div
              className="founder-image"
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
              variants={{
                hidden: { opacity: 0, x: -30 },
                visible: {
                  opacity: 1,
                  x: 0,
                  transition: { duration: 0.6 },
                },
              }}
            >
              <img src="/img/about.jpeg" alt="Founder" />
            </motion.div>

            <motion.div
              className="founder-bio"
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
              variants={{
                hidden: { opacity: 0, x: 30 },
                visible: {
                  opacity: 1,
                  x: 0,
                  transition: { duration: 0.6 },
                },
              }}
            >
              <p>
                Manatee Labs was founded to simplify and accelerate the
                transition to Amazon Connect in an era increasingly shaped by
                artificial intelligence. With a strong background in cloud
                architecture and automation, I built this company to help
                businesses unlock the full potential of AWS-powered contact
                centers—from seamless setup to intelligent call routing and deep
                analytics.
              </p>
              <p>
                I hold a Bachelor of Science from the University of Illinois at
                Urbana-Champaign and multiple advanced AWS certifications,
                including:
              </p>

              <div className="certifications">
                <ul className="certifications-list">
                  <li>AWS Certified Machine Learning – Specialty</li>
                  <li>AWS Certified Data Analytics – Specialty</li>
                  <li>
                    AWS Certified Solutions Architect – Professional & Associate
                  </li>
                  <li>AWS Certified DevOps Engineer – Professional</li>
                  <li>AWS Certified Developer – Associate</li>
                </ul>
              </div>

              <p className="founder-goal">
                My goal is to help businesses not only modernize their call
                center infrastructure but also build for the future with
                AI-first, scalable solutions rooted in AWS best practices.
              </p>
            </motion.div>
          </div>
        </div>
      </section>
      <CertificatesView />
    </div>
  );
};
